import { generateRandomId } from "../../../../../utils/Utils";

/**
 * Seeded lookup IDs (from warranty_type migration / DB).
 * Same pattern as artifact_type_id — never send type strings on write.
 *
 * warranty_vehicle: 1=GAS, 2=HYBRID, 3=EV, 4=DIESEL
 * warranty_classification: 1=MINOR, 2=MAJOR
 */
export const WARRANTY_VEHICLE_TYPE_ID = {
  GAS: 1,
  HYBRID: 2,
  EV: 3,
  DIESEL: 4,
};

export const WARRANTY_CLASSIFICATION_ID = {
  MINOR: 1,
  MAJOR: 2,
};

/** Legacy sentinel type — filter from chart bars if old rows exist in DB. */
export const TIME_WARRANTY_TYPE = {
  en: "TIME",
  es: "TIME",
};

export const MINOR_LABELS = [
  { en: "Adjustments", es: "Ajustes" },
  { en: "Emissions", es: "Emisiones" },
  { en: "Rust Through Corrosion", es: "Corrosión por Óxido" },
];

export const MAJOR_LABELS = [
  {
    en: "Battery",
    es: "Batería",
    defaultInfoTitle: { en: "Battery Warranty", es: "Garantía de Batería" },
    defaultInfoSubtitle: {
      en: "Covers the main battery and related components",
      es: "Cubre la batería principal y componentes relacionados",
    },
    defaultInfoFooter: {
      en: "Coverage applies to original factory-installed battery only.",
      es: "La cobertura aplica solo a la batería instalada de fábrica.",
    },
    row2FieldsEn: ["Engine", "Transmission", "Drivetrain"],
    row2FieldsEs: ["Motor", "Transmisión", "Tren de transmisión"],
  },
  {
    en: "Powertrain",
    es: "Tren Motriz",
    defaultInfoTitle: { en: "Powertrain Warranty", es: "Garantía del Tren Motriz" },
    defaultInfoSubtitle: {
      en: "Protects the key components that keep your vehicle moving",
      es: "Protege los componentes clave que mantienen su vehículo en movimiento",
    },
    defaultInfoFooter: {
      en: "Focuses on movement, not comfort, convenience, or technology.",
      es: "Se enfoca en el movimiento, no en comodidad, conveniencia o tecnología.",
    },
    row2FieldsEn: [
      "Engine",
      "Transmission",
      "Drivetrain",
    ],
    row2FieldsEs: [
      "Motor",
      "Transmisión",
      "Tren de transmisión",
    ],
  },
  {
    en: "Technology",
    es: "Tecnología",
    defaultInfoTitle: { en: "Technology Warranty", es: "Garantía de Tecnología" },
    defaultInfoSubtitle: {
      en: "Covers electronic systems and digital components",
      es: "Cubre sistemas electrónicos y componentes digitales",
    },
    defaultInfoFooter: {
      en: "Software updates may not be covered under this warranty.",
      es: "Las actualizaciones de software pueden no estar cubiertas.",
    },
    row2FieldsEn: [
      "Driver Assistance Systems",
      "Infotainment & Connectivity",
      "Control Modules & Sensors",
    ],
    row2FieldsEs: [
      "Sistemas de Asistencia al Conductor",
      "Infoentretenimiento y Conectividad",
      "Módulos de Control y Sensores",
    ],
  },
  {
    en: "Comprehensive",
    es: "Integral",
    defaultInfoTitle: { en: "Comprehensive Warranty", es: "Garantía Integral" },
    defaultInfoSubtitle: {
      en: "Broad coverage across major vehicle systems",
      es: "Cobertura amplia de los principales sistemas del vehículo",
    },
    defaultInfoFooter: {
      en: "See your owner's manual for complete coverage details.",
      es: "Consulte su manual del propietario para detalles completos.",
    },
    row2FieldsEn: [
      "Climate & Comfort",
      "Electrical Hardware",
      "Steering, Suspension & Braking",
    ],
    row2FieldsEs: [
      "Clima y Confort",
      "Hardware Eléctrico",
      "Dirección, Suspensión y Frenos",
    ],
  },
  {
    en: "Maintenance",
    es: "Mantenimiento",
    defaultInfoTitle: { en: "Maintenance Coverage", es: "Cobertura de Mantenimiento" },
    defaultInfoSubtitle: {
      en: "Covers scheduled maintenance and wear items",
      es: "Cubre mantenimiento programado y componentes de desgaste",
    },
    defaultInfoFooter: {
      en: "Maintenance must be performed at authorized service centers.",
      es: "El mantenimiento debe realizarse en centros de servicio autorizados.",
    },
    row2FieldsEn: [
      "Routine Maintenance",
      "Wear Components",
      "Scheduled Services",
    ],
    row2FieldsEs: [
      "Mantenimiento de Rutina",
      "Componentes de Desgaste",
      "Servicios Programados",
    ],
  },
];

export const TIME_LETTER_DEFAULTS = {
  T: {
    title: { en: "Tire Coverage Not Included", es: "Cobertura de Neumáticos No Incluida" },
    footer: {
      en: "Tire warranties are provided separately by the tire manufacturer.",
      es: "Las garantías de neumáticos son proporcionadas por separado por el fabricante.",
    },
    columns: [
      {
        headerEn: "What's Not Covered",
        headerEs: "Lo Que No Está Cubierto",
        itemsEn: "Road hazard damage\nPunctures & flats\nImproper inflation wear\nCurb damage",
        itemsEs: "Daño por peligros en la carretera\nPinchazos y ponchazos\nDesgaste por inflado incorrecto\nDaño por bordillo",
      },
      {
        headerEn: "What May Be Covered",
        headerEs: "Lo Que Puede Estar Cubierto",
        itemsEn: "Manufacturer defects\nWorkmanship issues\nTread wear (with warranty)",
        itemsEs: "Defectos del fabricante\nProblemas de fabricación\nDesgaste de banda (con garantía)",
      },
    ],
  },
  I: {
    title: { en: "Interior Items Not Covered", es: "Artículos Interiores No Cubiertos" },
    footer: {
      en: "Factory warranties cover defects in materials and workmanship. Interior wear from normal use is typically not covered.",
      es: "Las garantías de fábrica cubren defectos en materiales y mano de obra. El desgaste interior por uso normal no está cubierto.",
    },
    columns: [
      {
        headerEn: "Interior Items Not Covered",
        headerEs: "Artículos Interiores No Cubiertos",
        itemsEn: "Spills\nStains\nTears\nBurns\nRips",
        itemsEs: "Derrames\nManchas\nRasgaduras\nQuemaduras\nRoturas",
      },
      {
        headerEn: "Exterior Items Not Covered",
        headerEs: "Artículos Exteriores No Cubiertos",
        itemsEn: "Fading\nOxidation\nAcid Rain\nTree Sap\nBird Droppings",
        itemsEs: "Decoloración\nOxidación\nLluvia Ácida\nSavia de Árboles\nExcrementos de Aves",
      },
    ],
  },
  M: {
    title: { en: "Maintenance Items Not Covered", es: "Artículos de Mantenimiento No Cubiertos" },
    footer: {
      en: "Regular maintenance is the owner's responsibility. Failure to maintain may void warranty coverage.",
      es: "El mantenimiento regular es responsabilidad del propietario. No mantener puede anular la cobertura.",
    },
    columns: [
      {
        headerEn: "Owner Responsibility",
        headerEs: "Responsabilidad del Propietario",
        itemsEn: "Oil changes\nFilter replacements\nBrake pads\nWiper blades\nBulbs & fuses",
        itemsEs: "Cambios de aceite\nReemplazo de filtros\nPastillas de freno\nLimpiaparabrisas\nFocos y fusibles",
      },
      {
        headerEn: "Wear & Tear Items",
        headerEs: "Artículos de Desgaste",
        itemsEn: "Clutch linings\nBrake rotors\nSpark plugs\nBelts & hoses\nBattery (12V)",
        itemsEs: "Revestimientos de embrague\nRotores de freno\nBujías\nCorreas y mangueras\nBatería (12V)",
      },
    ],
  },
  E: {
    title: { en: "Environmental Damage Not Covered", es: "Daño Ambiental No Cubierto" },
    footer: {
      en: "Factory warranties cover defects in materials and workmanship. Environmental damage to the interior or exterior is typically not covered. See your owner's manual for more details.",
      es: "Las garantías de fábrica cubren defectos en materiales y mano de obra. El daño ambiental al interior o exterior generalmente no está cubierto. Consulte su manual del propietario.",
    },
    columns: [
      {
        headerEn: "Interior Items Not Covered",
        headerEs: "Artículos Interiores No Cubiertos",
        itemsEn: "Spills\nStains\nTears\nBurns\nRips",
        itemsEs: "Derrames\nManchas\nRasgaduras\nQuemaduras\nRoturas",
      },
      {
        headerEn: "Exterior Items Not Covered",
        headerEs: "Artículos Exteriores No Cubiertos",
        itemsEn: "Fading\nOxidation\nAcid Rain\nTree Sap\nBird Droppings",
        itemsEs: "Decoloración\nOxidación\nLluvia Ácida\nSavia de Árboles\nExcrementos de Aves",
      },
    ],
  },
};

/** One Covered Items (Info) column — maps to warranty_information_items. */
const makeInfoItem = (defaults = {}) => ({
  id: generateRandomId(6),
  information_item_id: null,
  coverage_category_en: defaults.headerEn || "",
  coverage_category_es: defaults.headerEs || "",
  coverage_items_en: defaults.itemsEn || "",
  coverage_items_es: defaults.itemsEs || "",
});

/** One TIME popup table column — maps to warranty_exclusion_column. */
const makeExclusionColumn = (defaults = {}) => ({
  id: generateRandomId(6),
  column_id: null,
  column_header_en: defaults.headerEn || "",
  column_header_es: defaults.headerEs || "",
  column_items_en: defaults.itemsEn || "",
  column_items_es: defaults.itemsEs || "",
  sort_order: defaults.sort_order ?? 0,
});

/** One Uncovered Items (TIME) letter — maps to warranty_exclusion. */
const makeExclusionItem = (letter = "") => {
  const defaults = TIME_LETTER_DEFAULTS[letter] || {};
  const defaultColumns = (defaults.columns || []).map((col, index) =>
    makeExclusionColumn({ ...col, sort_order: index })
  );

  return {
    id: generateRandomId(6),
    exclusion_id: null,
    letter,
    exclusion_category_en: defaults.title?.en || "",
    exclusion_category_es: defaults.title?.es || "",
    exclusion_items_en: defaults.footer?.en || "",
    exclusion_items_es: defaults.footer?.es || "",
    what_to_know_text_en: defaults.title?.en || "",
    what_to_know_text_es: defaults.title?.es || "",
    description_text_en: defaults.footer?.en || "",
    description_text_es: defaults.footer?.es || "",
    title_en: defaults.title?.en || "",
    title_es: defaults.title?.es || "",
    footer_en: defaults.footer?.en || "",
    footer_es: defaults.footer?.es || "",
    columns: defaultColumns.length ? defaultColumns : [makeExclusionColumn()],
  };
};

/**
 * Build default I-popup info items (columns) from a major label's row2Fields.
 */
const makeDefaultInfoItems = (label) => {
  if (!label?.row2FieldsEn?.length) return [makeInfoItem()];
  return label.row2FieldsEn.map((headerEn, index) => {
    const headerEs = label.row2FieldsEs?.[index] || "";
    return makeInfoItem({ headerEn, headerEs });
  });
};

const makeCoverageRow = (
  vehicleTypeId,
  classificationId,
  typeEn,
  typeEs,
  { withInfo = false, label = null } = {}
) => ({
  id: generateRandomId(6),
  coverage_id: null,
  warranty_vehicle_type_id: vehicleTypeId,
  warranty_classification_id: classificationId,
  warranty_type_en: typeEn,
  warranty_type_es: typeEs,
  coverage_months: "",
  coverage_miles: "",
   is_enabled: true, 
  info_title_en: label?.defaultInfoTitle?.en || "",
  info_title_es: label?.defaultInfoTitle?.es || "",
  info_subtitle_en: label?.defaultInfoSubtitle?.en || "",
  info_subtitle_es: label?.defaultInfoSubtitle?.es || "",
  info_footer_en: label?.defaultInfoFooter?.en || "",
  info_footer_es: label?.defaultInfoFooter?.es || "",
  infoItems: withInfo ? makeDefaultInfoItems(label) : [],
});

const makeCoverageSection = (vehicleTypeId) => ({
  minor: MINOR_LABELS.map((label) =>
    makeCoverageRow(
      vehicleTypeId,
      WARRANTY_CLASSIFICATION_ID.MINOR,
      label.en,
      label.es
    )
  ),
  major: MAJOR_LABELS.map((label) =>
    makeCoverageRow(
      vehicleTypeId,
      WARRANTY_CLASSIFICATION_ID.MAJOR,
      label.en,
      label.es,
      { withInfo: true, label }
    )
  ),
  time: {
    fields: ["T", "I", "M", "E"].map((letter) => makeExclusionItem(letter)),
  },
});

/** Parent warranty state — mirrors ArtifactsParent `artifacts` shape. */
export const createInitialWarrantyState = (dealershipId = "") => ({
  dealership_id: dealershipId,
  gas: makeCoverageSection(WARRANTY_VEHICLE_TYPE_ID.GAS),
  hybrid: makeCoverageSection(WARRANTY_VEHICLE_TYPE_ID.HYBRID),
  ev: makeCoverageSection(WARRANTY_VEHICLE_TYPE_ID.EV),
  diesel: makeCoverageSection(WARRANTY_VEHICLE_TYPE_ID.DIESEL),
});

export const COVERAGE_SECTION_KEYS = ["gas", "hybrid", "ev", "diesel"];

export const SECTION_VEHICLE_TYPE_ID = {
  gas: WARRANTY_VEHICLE_TYPE_ID.GAS,
  hybrid: WARRANTY_VEHICLE_TYPE_ID.HYBRID,
  ev: WARRANTY_VEHICLE_TYPE_ID.EV,
  diesel: WARRANTY_VEHICLE_TYPE_ID.DIESEL,
};

export { makeInfoItem, makeExclusionItem, makeExclusionColumn };

const SECTION_KEY_BY_VEHICLE_TYPE_ID = {
  [WARRANTY_VEHICLE_TYPE_ID.GAS]: "gas",
  [WARRANTY_VEHICLE_TYPE_ID.HYBRID]: "hybrid",
  [WARRANTY_VEHICLE_TYPE_ID.EV]: "ev",
  [WARRANTY_VEHICLE_TYPE_ID.DIESEL]: "diesel",
};

const matchCoverageRow = (row, coverage) => {
  if (row.coverage_id != null && Number(row.coverage_id) === Number(coverage.id)) {
    return true;
  }

  return (
    row.warranty_vehicle_type_id === coverage.warranty_vehicle_type_id &&
    row.warranty_classification_id === coverage.warranty_classification_id &&
    row.warranty_type_en === coverage.warranty_type_en
  );
};

const applyCoverageMatch = (row, match) => {
  if (!match) return row;

  const infoItems = match.information_items?.length
    ? match.information_items.map((item) => ({
        ...makeInfoItem(),
        information_item_id: item.id,
        coverage_category_en: item.coverage_category_en || "",
        coverage_category_es: item.coverage_category_es || "",
        coverage_items_en: item.coverage_items_en || "",
        coverage_items_es: item.coverage_items_es || "",
        
      }))
    : row.infoItems;

  return {
    ...row,
    coverage_id: match.id,
    warranty_type_en: match.warranty_type_en ?? row.warranty_type_en,
    warranty_type_es: match.warranty_type_es ?? row.warranty_type_es,
    coverage_months: match.coverage_months ?? "",
    coverage_miles: match.coverage_miles ?? "",
    info_title_en: match.info_title_en || "",
    info_title_es: match.info_title_es || "",
    info_subtitle_en: match.info_subtitle_en || "",
    info_subtitle_es: match.info_subtitle_es || "",
    info_footer_en: match.info_footer_en || "",
    info_footer_es: match.info_footer_es || "",
    is_enabled: match.is_enabled !== false, 
    infoItems,
  };
};

const findCoverageMatch = (row, coverages, usedCoverageIds) => {
  const byId = coverages.find(
    (c) =>
      row.coverage_id != null &&
      Number(row.coverage_id) === Number(c.id) &&
      !usedCoverageIds.has(c.id)
  );
  if (byId) return byId;

  const byLabel = coverages.find(
    (c) => matchCoverageRow(row, c) && !usedCoverageIds.has(c.id)
  );
  if (byLabel) return byLabel;

  // Renamed labels: fall back to next unused row in the same classification slot.
  return coverages.find(
    (c) =>
      !usedCoverageIds.has(c.id) &&
      c.warranty_vehicle_type_id === row.warranty_vehicle_type_id &&
      c.warranty_classification_id === row.warranty_classification_id
  );
};

/**
 * Merge fetched coverages + exclusions into admin warranty state (preserves DB ids).
 */
export const mergeWarrantyFromFetch = (
  warrantyState,
  coverages = [],
  exclusions = [],
  vehicleTypeId
) => {
  const sectionKey = SECTION_KEY_BY_VEHICLE_TYPE_ID[vehicleTypeId];
  if (!sectionKey || !warrantyState?.[sectionKey]) return warrantyState;

  const section = warrantyState[sectionKey];
  const usedCoverageIds = new Set();

  const matchForRow = (row) => {
    const match = findCoverageMatch(row, coverages, usedCoverageIds);
    if (match) usedCoverageIds.add(match.id);
    return match;
  };

  const nextSection = {
    ...section,
    minor: section.minor.map((row) => applyCoverageMatch(row, matchForRow(row))),
    major: section.major.map((row) => applyCoverageMatch(row, matchForRow(row))),
    time: {
      fields: section.time.fields.map((field) => {
        const match = exclusions.find(
          (ex) =>
            ex.uncoverage_type === field.letter ||
            ex.uncoverage_type === field.uncoverage_type
        );
        if (!match) return field;

        const columns = match.columns?.length
          ? match.columns.map((col, index) => ({
              ...makeExclusionColumn(),
              column_id: col.id,
              column_header_en: col.column_header_en || "",
              column_header_es: col.column_header_es || "",
              column_items_en: col.column_items_en || "",
              column_items_es: col.column_items_es || "",
              sort_order: col.sort_order ?? index,
            }))
          : field.columns?.length
            ? field.columns
            : [makeExclusionColumn()];

        return {
          ...field,
          exclusion_id: match.id,
          what_to_know_text_en: match.exclusion_category_en || "",
          what_to_know_text_es: match.exclusion_category_es || "",
          description_text_en: match.exclusion_items_en || "",
          description_text_es: match.exclusion_items_es || "",
          exclusion_category_en: match.exclusion_category_en || "",
          exclusion_category_es: match.exclusion_category_es || "",
          exclusion_items_en: match.exclusion_items_en || "",
          exclusion_items_es: match.exclusion_items_es || "",
          title_en: match.title_en || match.exclusion_category_en || "",
          title_es: match.title_es || match.exclusion_category_es || "",
          footer_en: match.footer_en || match.exclusion_items_en || "",
          footer_es: match.footer_es || match.exclusion_items_es || "",
          columns,
        };
      }),
    },
  };

  return { ...warrantyState, [sectionKey]: nextSection };
};

/**
 * Merge a full fetch (all vehicle types) into admin warranty state.
 */
export const mergeAllWarrantyFromFetch = (
  warrantyState,
  coverages = [],
  exclusions = []
) => {
  let merged = warrantyState;

  for (const vehicleTypeId of Object.values(WARRANTY_VEHICLE_TYPE_ID)) {
    const vehicleCoverages = coverages.filter(
      (c) => c.warranty_vehicle_type_id === vehicleTypeId
    );
    const vehicleExclusions = exclusions.filter(
      (e) => e.warranty_vehicle_type_id === vehicleTypeId
    );

    merged = mergeWarrantyFromFetch(
      merged,
      vehicleCoverages,
      vehicleExclusions,
      vehicleTypeId
    );
  }

  return merged;
};

const mapInformationItems = (infoItems = []) =>
  (infoItems || []).map((item) => ({
    id: item.information_item_id ?? null,
    coverage_category_en: item.coverage_category_en || null,
    coverage_category_es: item.coverage_category_es || null,
    coverage_items_en: item.coverage_items_en || null,
    coverage_items_es: item.coverage_items_es || null,
  }));

const mapExclusionColumns = (columns = []) =>
  (columns || []).map((col, index) => ({
    id: col.column_id ?? null,
    column_header_en: col.column_header_en || null,
    column_header_es: col.column_header_es || null,
    column_items_en: col.column_items_en || null,
    column_items_es: col.column_items_es || null,
    sort_order: col.sort_order ?? index,
  }));

const mapExclusions = (fields = []) =>
  (fields || []).map((item) => ({
    id: item.exclusion_id ?? null,
    uncoverage_type: item.letter || item.uncoverage_type || null,
    exclusion_category_en:
      item.what_to_know_text_en || item.exclusion_category_en || null,
    exclusion_category_es:
      item.what_to_know_text_es || item.exclusion_category_es || null,
    exclusion_items_en:
      item.description_text_en || item.exclusion_items_en || null,
    exclusion_items_es:
      item.description_text_es || item.exclusion_items_es || null,
    what_to_know_text_en:
      item.what_to_know_text_en || item.exclusion_category_en || null,
    what_to_know_text_es:
      item.what_to_know_text_es || item.exclusion_category_es || null,
    description_text_en:
      item.description_text_en || item.exclusion_items_en || null,
    description_text_es:
      item.description_text_es || item.exclusion_items_es || null,
    title_en: item.title_en || null,
    title_es: item.title_es || null,
    footer_en: item.footer_en || null,
    footer_es: item.footer_es || null,
    columns: mapExclusionColumns(item.columns),
  }));

/**
 * Build one adminWarranty payload (coverage rows + vehicle exclusions).
 */
export const buildWarrantySaveInput = (warranty) => {
  const dealershipId = warranty?.dealership_id;
  const coverages = [];
  const vehicle_exclusions = [];

  for (const key of COVERAGE_SECTION_KEYS) {
    const section = warranty?.[key];
    if (!section) continue;

    [...(section.minor || []), ...(section.major || [])].forEach((row) => {
      coverages.push({
        id: row.coverage_id ?? null,
        warranty_vehicle_type_id: row.warranty_vehicle_type_id,
        warranty_classification_id: row.warranty_classification_id,
        warranty_type_en: row.warranty_type_en,
        warranty_type_es: row.warranty_type_es,
        coverage_months: toNullableInt(row.coverage_months),
        coverage_miles: toNullableInt(row.coverage_miles),
        info_title_en: row.info_title_en || null,
        info_title_es: row.info_title_es || null,
        info_subtitle_en: row.info_subtitle_en || null,
        info_subtitle_es: row.info_subtitle_es || null,
        info_footer_en: row.info_footer_en || null,
        info_footer_es: row.info_footer_es || null,
        is_enabled: row.is_enabled !== false, 
        information_items: mapInformationItems(row.infoItems),
      });
    });

    vehicle_exclusions.push({
      warranty_vehicle_type_id: SECTION_VEHICLE_TYPE_ID[key],
      exclusions: mapExclusions(section.time?.fields),
    });
  }

  return {
    dealership_id: dealershipId,
    coverages,
    vehicle_exclusions,
  };
};

const toNullableInt = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};
